import { AppFactory, AppPluginInterface, catchError } from "@opendash/core";
import { registerIconPack } from "@opendash/icons";
import { $monitoring } from "@opendash/plugin-monitoring";
import {
  $parse,
  StringColorDisplay,
  StringColorEdit,
  TextareaField,
  TimespanField,
} from "@opendash/plugin-parse";
import { Company, Contact } from "@opendash/plugin-parse/dist/types-generated";
import { ColorPicker, CronForm } from "@opendash/ui";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import Parse from "parse";
import React from "react";
import { TypeSelect } from "./features/administration/ticket_creation/components/inputs/TypeSelect";
import { TicketFormConfigParseField } from "./features/administration/ticket_creation/components/TicketFormConfigParseField";
import {
  ScheduleMetaFormItems,
  TemplateHeaderExtra,
} from "./features/schedules";
import { StepMetaButton } from "./features/schedules/components/admin/StepMetaButton";
import { GlobalTicketModal } from "./features/tickets/creation/components/GlobalTicketModal";
import { QRCodeInput } from "./features/tickets/qrcode/components/QRCodeInput";
import {
  Issuecategory,
  Item,
  Project,
  Schedule_Step,
  Schedule_Template,
  Source_File,
  SourceMeta,
  Ticket_QR_Code,
} from "./js/parsegenerated";
import { Ticket_FormConfig } from "./js/parsegenerated/Ticket_FormConfig";
import { GeneralManagePage } from "./pages/Administration/GeneralManagePage";
import { KanbanManagePage } from "./pages/Administration/KanbanManagePage";
import { ItemsPage } from "./pages/Items/ItemsPage";
import KnowledgeAdminPage from "./pages/Knowledge/KnowledgeAdminPage";
import KnowledgeOverviewPage from "./pages/Knowledge/KnowledgeOverviewPage";
import { MachinelogPage } from "./pages/Machinelog/MachinelogPage";
import { MonitorPage } from "./pages/Monitor/MonitorPage";
import { MonitoringPage } from "./pages/Monitoring/MonitoringPage";
import { ScheduleCalendarPage } from "./pages/Schedule/ScheduleCalendar";
import { ScheduleExecutionPage } from "./pages/Schedule/ScheduleExecutionPage";
import { ScheduleOverviewPage } from "./pages/Schedule/ScheduleOverviewPage";
import { ScheduleSourcePage } from "./pages/Schedule/ScheduleSourcePage";
import { SourceMetaFilterPage } from "./pages/Schedule/SourceMetaFilterPage";
import { StatisticsPage } from "./pages/Stats/StatsPage";
import { ProjectPage } from "./pages/Ticket/ProjectPage";
import { ProjectsOverviewPage } from "./pages/Ticket/ProjectsOverviewPage";
import { QRCodePrintPage } from "./pages/Ticket/QRCodePrintPage";
import { QRCodeScanPage } from "./pages/Ticket/QRCodeScanPage";
import { TicketCreatePage } from "./pages/Ticket/TicketCreatePage";
import { TicketDetailPage } from "./pages/Ticket/TicketDetailPage";
import { TicketsPage } from "./pages/Ticket/TicketsPage";
import { OpenService } from "./types/config";
import { default as meanTimeBetweenFailures } from "./widgets/mean-time-between-failures";
import { default as numberOfIssues } from "./widgets/number-of-issues";

export class OpenServicePlugin implements AppPluginInterface {
  name: string = "OpenServicePlugin";

  constructor() {}

  async onFactory(factory: AppFactory) {
    // await $parse.server.lock.wait();

    registerIconPack(
      (await import("@opendash/icons/dist/fa-light.json")) as any
    );

    if (!$parse.server.config.FEATURE_MAINTENANCE) {
      return;
    }
    // Translations:
    factory.registerTranslationResolver(
      "en",
      "maintenance",
      async () => await import("./translations/en.json")
    );

    factory.registerTranslationResolver(
      "de",
      "maintenance",
      async () => await import("./translations/de.json")
    );
    factory.registerTranslationResolver(
      "nl",
      "maintenance",
      async () => await import("./translations/nl.json")
    );

    dayjs.extend(weekOfYear);

    $parse.ui.setClassConfig<Project>({
      className: Project.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["name"],
      displayFields: [
        "name",
        "description",
        "start",
        "end",
        "parent",
        "assignedRoles",
        "assignedUsers",
        "finished",
      ],
      createFields: [
        "name",
        "description",
        "start",
        "end",
        "parent",
        "color",
        "icon",
      ],
      editFields: [
        "name",
        "description",
        "start",
        "end",
        "parent",
        "color",
        "icon",
        "finished",
      ],
    });

    $parse.ui.setDefaultView(Project.className, {
      type: "table",
    });

    $parse.ui.setClassConfig<Company>({
      className: Company.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["name"],
      displayFields: ["name", "contacts", "sources"],
      createFields: ["name"],
      editFields: ["name", "contacts", "sources"],
    });

    $parse.ui.setDefaultView(Company.className, {
      type: "table",
      showSearch: true,
    });

    $parse.ui.setClassConfig<Contact>({
      className: Contact.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["lastname", "firstname"],
      displayFields: [
        "firstname",
        "lastname",
        "street",
        "postal",
        "city",
        "department",
        "position",
        "email",
        "phone",
        "website",
        "description",
      ],
      createFields: [
        "firstname",
        "lastname",
        "street",
        "postal",
        "city",
        "department",
        "position",
        "email",
        "phone",
        "website",
        "description",
      ],
      editFields: [
        "firstname",
        "lastname",
        "street",
        "postal",
        "city",
        "department",
        "position",
        "email",
        "phone",
        "website",
        "description",
      ],
    });

    factory.registerGlobalComponent(GlobalTicketModal);

    $parse.ui.setDefaultView(Contact.className, {
      type: "table",
      showSearch: true,
    });

    $parse.ui.setClassConfig<Schedule_Template>({
      className: Schedule_Template.className,
      translationPrefix: "maintenance:classes.",
      customFields: {
        description: (obj) =>
          React.createElement(TextareaField, {
            value: obj.value,
            setValue: obj.setValue,
          }),

        cron: (obj) =>
          React.createElement(CronForm, {
            value: obj.value,
            setValue: obj.setValue,
          }),

        color: (obj) => {
          if (obj.setValue)
            return React.createElement(StringColorEdit, {
              isRequired: false,
              isNullable: true,
              value: obj.value,
              setValue: obj.setValue,
            });
          else
            return React.createElement(StringColorDisplay, {
              value: obj.value,
            });
        },
        notifyBeforeDue: (obj) =>
          React.createElement(TimespanField, {
            value: obj.value,
            setValue: obj.setValue,
          }),
      },
      titleFields: ["title"],
      displayFields: [
        "enabled",
        "title",
        "description",
        "sources",
        "notifyBeforeDue",
        "steps",
        "cron",
        "color",
      ],
      createFields: [
        "title",
        "description",
        "color",
        "notifyBeforeDue",
        "cron",
        "sources",
        "steps",
      ],
      editFields: [
        "enabled",
        "title",
        "description",
        "sources",
        "notifyBeforeDue",
        "steps",
        "cron",
        "color",
      ],
    });

    $parse.ui.setDefaultView(Schedule_Template.className, {
      type: "table",
      showSearch: true,
      headerextra: <TemplateHeaderExtra />,
    });

    $parse.ui.setClassConfig<Schedule_Template>({
      className: Schedule_Template.className,
      translationPrefix: "maintenance:classes.",
      customFields: {
        description: (obj) =>
          React.createElement(TextareaField, {
            value: obj.value,
            setValue: obj.setValue,
          }),

        cron: (obj) =>
          React.createElement(CronForm, {
            value: obj.value,
            setValue: obj.setValue,
          }),
        color: (obj) => {
          if (obj.setValue)
            return React.createElement(
              ColorPicker,
              {
                value: obj.value,
                onChange: obj.setValue,
              },
              null
            );
          else return obj.value;
        },
        notifyBeforeDue: (obj) =>
          React.createElement(
            TimespanField,
            {
              value: obj.value || {},
              setValue: obj.setValue,
            },
            null
          ),
      },
      titleFields: ["title"],
      displayFields: [
        "enabled",
        "title",
        "description",
        "sources",
        "notifyBeforeDue",
        "steps",
        "cron",
        "color",
      ],
      createFields: [
        "title",
        "description",
        "color",
        "notifyBeforeDue",
        "cron",
        "sources",
        "steps",
      ],
      editFields: [
        "enabled",
        "title",
        "description",
        "sources",
        "notifyBeforeDue",
        "steps",
        "cron",
        "color",
      ],
    });

    $parse.ui.setDefaultView(Schedule_Template.className, {
      type: "table",
      showSearch: true,
    });

    $parse.ui.setClassConfig<Schedule_Step>({
      className: Schedule_Step.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["title"],
      displayFields: ["title", "description", "roles", "fields"],
      createFields: ["title", "description"],
      editFields: ["title", "description", "roles", "fields"],
      customFields: {
        fields: (obj) =>
          React.createElement(ScheduleMetaFormItems, {
            value: obj.value,
            setValue: obj.setValue,
          }),
      },
    });

    $parse.ui.setDefaultView(Schedule_Step.className, {
      type: "table",
      showSearch: true,
      additionalActions: React.createElement(StepMetaButton),
    });

    $parse.ui.setClassConfig<Issuecategory>({
      className: Issuecategory.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["name"],
      beforeDelete: async (objectId) => {
        for (const id of objectId) {
          const issue = await new Parse.Query(Issuecategory).get(id);
          await issue.save({ enabled: false });
        }
        return false;
      },
      displayFields: [
        "name",
        "issuecode",
        "enabled",
        "icon",
        "catchall",
        "parent",
        "source",
      ],
      createFields: [
        "enabled",
        "catchall",
        "issuecode",
        "name",
        "icon",
        "parent",
      ],
      editFields: [
        "enabled",
        "catchall",
        "issuecode",
        "name",
        "icon",
        "parent",
        "source",
      ],
    });

    $parse.ui.setDefaultView(Issuecategory.className, {
      type: "table",
      showSearch: true,
    });

    $parse.ui.setClassConfig<Item>({
      className: Item.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["name"],
      displayFields: [
        "name",
        "description",
        "sources",
        "category",
        "internalNumber",
        "price",
        "stock",
        "stockUnit",
        "stockAlarmAt",
        "orderNumber",
        "supplier",
        "storageLocation",
      ],
      createFields: [
        "name",
        "description",
        "category",
        "internalNumber",
        "price",
        "stock",
        "stockUnit",
        "stockAlarmAt",
        "orderNumber",
        "supplier",
        "storageLocation",
      ],
      editFields: [
        "name",
        "description",
        "sources",
        "category",
        "internalNumber",
        "price",
        "stock",
        "stockUnit",
        "stockAlarmAt",
        "orderNumber",
        "supplier",
        "storageLocation",
      ],
    });

    $parse.ui.setDefaultView(Item.className, {
      type: "table",
      showSearch: true,
    });

    $parse.ui.setClassConfig<Ticket_FormConfig>({
      className: Ticket_FormConfig.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["view"],
      displayFields: ["view", "config"],
      createFields: ["view", "config"],
      editFields: ["view", "config"],
      customFields: {
        view: (obj) =>
          React.createElement(TypeSelect, {
            value: obj.value,
            onChange: obj.setValue,
          }),
        config: (obj) =>
          React.createElement(TicketFormConfigParseField, {
            value: obj.value,
            setValue: obj.setValue,
          }),
      },
    });

    $parse.ui.setDefaultView(Ticket_FormConfig.className, {
      type: "table",
      showSearch: true,
    });

    $parse.ui.setClassConfig<Ticket_QR_Code>({
      className: Ticket_QR_Code.className,
      translationPrefix: "maintenance:classes.",
      titleFields: ["name"],
      displayFields: ["name", "code"],
      createFields: ["name", "code", "user"],
      editFields: ["name", "code", "user"],
      customFields: {
        code: (obj) =>
          React.createElement(QRCodeInput, {
            value: obj.value,
            onChange: obj.setValue,
            object: obj.parseObject as Ticket_QR_Code,
          }),
      },
    });

    $parse.ui.setDefaultView(Ticket_QR_Code.className, {
      type: "table",
      showSearch: true,
    });

    // Widgets:
    if ($parse.server.config.FEATURE_MAINTENANCE) {
      //REMOVE WHEN GOING PRODUCTIVE
      if (window.location.hostname === "localhost") {
        $monitoring.registerWidget(numberOfIssues);
        $monitoring.registerWidget(meanTimeBetweenFailures);
      }
    }

    // Routes:
    const routes: Array<{
      path: string;
      component: React.FunctionComponent<object>;
      permission?: string | string[];
      hideSourcePicker?: boolean;
    }> = [
      // {
      //   path: "/openservice",
      //   component: OverviewPage,
      //   permission: OpenService.Permissions.Access.global,
      // },
      {
        path: "/admin/openservice/knowledge/manuals",
        component: KnowledgeAdminPage,
        permission: [
          OpenService.Permissions.Access.global,
          OpenService.Permissions.Access.monitor,
        ],
      },
      {
        path: "/openservice/knowledge/manuals",
        component: KnowledgeOverviewPage,
        permission: [
          OpenService.Permissions.Access.global,
          OpenService.Permissions.Access.monitor,
        ],
      },
      {
        path: "/openservice/tickets",
        component: TicketsPage,
        permission: OpenService.Permissions.Access.global,
        hideSourcePicker: true,
      },
      {
        path: "/openservice/ticket/:id",
        component: TicketDetailPage,
        permission: OpenService.Permissions.Access.global,
        hideSourcePicker: true,
      },
      {
        path: "/openservice/monitor",
        component: MonitorPage,
        permission: [
          OpenService.Permissions.Access.global,
          OpenService.Permissions.Access.monitor,
        ],
        hideSourcePicker: true,
      },
      {
        path: "/openservice/inventory/items",
        component: ItemsPage,
        permission: [OpenService.Permissions.Access.global],
      },
      {
        path: "/openservice/schedule",
        component: ScheduleSourcePage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/schedule/overview",
        component: ScheduleOverviewPage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/schedule/templates",
        component: ScheduleSourcePage,
        permission: [OpenService.Permissions.Access.global],
      },
      {
        path: "/openservice/schedule/:id/execute",
        component: ScheduleExecutionPage,
        permission: OpenService.Permissions.Access.global,
        hideSourcePicker: true,
      },
      {
        path: "/openservice/schedule/calendar",
        component: ScheduleCalendarPage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/schedule/sourcemeta",
        component: SourceMetaFilterPage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/statistics",
        component: StatisticsPage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/machinelog",
        component: MachinelogPage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/ticket/create",
        component: TicketCreatePage,
        permission: [
          OpenService.Permissions.Access.global,
          OpenService.Permissions.Create.ticket,
        ],
      },
      {
        path: "/openservice/ticket/create_qr",
        component: QRCodeScanPage,
        permission: [OpenService.Permissions.Access.global],
      },
      {
        path: "/admin/openservice/monitor",
        component: KanbanManagePage,
        permission: [
          OpenService.Permissions.Access.global,
          OpenService.Permissions.Access.monitor,
        ],
      },
      {
        path: "/admin/openservice/general",
        component: GeneralManagePage,
        permission: OpenService.Permissions.Access.global,
      },
      {
        path: "/openservice/projects",
        component: ProjectsOverviewPage,
        permission: OpenService.Permissions.Access.global,
        hideSourcePicker: true,
      },
      {
        path: "/openservice/project/:id",
        component: ProjectPage,
        permission: OpenService.Permissions.Access.global,
        hideSourcePicker: true,
      },
      {
        path: "/admin/parse/print/:id",
        component: QRCodePrintPage,
        permission: [OpenService.Permissions.Access.global],
        hideSourcePicker: true,
      },
    ];

    if ($parse.server.config.FEATURE_MONITORING) {
      routes.push({
        path: "/openservice/monitoring",
        component: MonitoringPage,
        permission: [
          OpenService.Permissions.Access.global,
          OpenService.Permissions.Access.monitoring,
        ],
      });
    }

    routes.forEach((route) => {
      factory.registerRoute({
        path: route.path,
        component: async () => ({ default: route.component }),
        permission: route.permission,
        headerSourcePicker: !route.hideSourcePicker,
      });
    });

    // Navigation
    // Frontpage
    factory.registerStaticNavigationItem({
      id: "service",
      group: "service",
      place: "frontpage",
      order: 10,
      color: "#7fbb47",
      label: "Service",
      icon: "fa:wrench",
      link: "/openservice/tickets",
      routeCondition: "**",
      activeCondition: "/openservice/tickets",
      permission: OpenService.Permissions.Access.global,
    });

    // Left menu
    factory.registerStaticNavigationGroup({
      id: "tickets",
      label: "maintenance:menu.tickets.group_label",
      icon: "fa:hammer",
      order: 20,
    });

    factory.registerStaticNavigationGroup({
      id: "statistics",
      label: "maintenance:menu.statistics.group_label",
      icon: "fa:analytics",
      order: 30,
    });

    factory.registerStaticNavigationGroup({
      id: "schedules",
      label: "maintenance:menu.schedules.group_label",
      icon: "fa:ballot-check",
      order: 40,
    });

    factory.registerStaticNavigationGroup({
      id: "openservice_admin",
      label: "maintenance:menu.administration.group_label",
      icon: "fa:sliders-h",
      order: 50,
    });

    factory.registerStaticNavigationGroup({
      id: "inventory",
      label: "maintenance:menu.inventory.group_label",
      icon: "fa:inventory",
      order: 60,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/ticket/create",
      group: null,
      place: "sidebar",
      order: 2,
      label: "maintenance:menu.tickets.create_label",
      icon: "fa:plus",
      link: "/openservice/ticket/create",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/ticket/create",
      permission: [
        OpenService.Permissions.Access.global,
        OpenService.Permissions.Create.ticket,
      ],
    });

    factory.registerStaticNavigationItem({
      id: "/openservice/ticket/create_qr",
      group: null,
      place: "sidebar",
      order: 3,
      label: "maintenance:menu.tickets.create_qr_label",
      icon: "fa:plus",
      link: "/openservice/ticket/create_qr",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/ticket/create_qr",
      permission: [
        OpenService.Permissions.Access.global,
        OpenService.Permissions.Create.ticket,
      ],
    });

    factory.registerStaticNavigationItem({
      id: "openservice/tickets",
      group: "tickets",
      place: "sidebar",
      order: 10,
      label: "maintenance:menu.tickets.tickets_label",
      icon: "fa:ticket",
      link: "/openservice/tickets",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/tickets",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/monitor",
      group: "tickets",
      place: "sidebar",
      order: 10,
      label: "maintenance:menu.tickets.monitor_label",
      icon: "fa:tv",
      link: "/openservice/monitor",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/monitor",
      permission: [
        OpenService.Permissions.Access.global,
        OpenService.Permissions.Access.monitor,
      ],
    });

    // factory.registerStaticNavigationItem({
    //   id: "openservice/schedule",
    //   group: "schedules",
    //   place: "sidebar",
    //   order: 10,
    //   label: "maintenance:menu.schedules.schedule_label",
    //   icon: "fa:calendar-check",
    //   link: "/openservice/schedule",
    //   routeCondition: "/openservice/**",
    //   activeCondition: "/openservice/schedule",
    //   permission: OpenService.Permissions.Access.global,
    // });

    factory.registerStaticNavigationItem({
      id: "openservice/schedule/overview",
      group: "schedules",
      place: "sidebar",
      order: 9,
      label: "maintenance:navigation.schedule.overview.title",
      icon: "fa:project-diagram",
      link: "/openservice/schedule/overview",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/schedule/overview",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/schedule/calendar",
      group: "schedules",
      place: "sidebar",
      order: 10,
      label: "maintenance:menu.schedules.calendar_label",
      icon: "fa:calendar",
      link: "/openservice/schedule/calendar",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/schedule/calendar",
      permission: OpenService.Permissions.Access.global,
    });

    try {
      const sourceMeta = await new Parse.Query(SourceMeta).first();
      if (sourceMeta)
        factory.registerStaticNavigationItem({
          id: "openservice/schedule/sourcemeta",
          group: "schedules",
          place: "sidebar",
          order: 10,
          label: "maintenance:menu.schedules.sourcemeta.label",
          icon: "fa:wrench",
          link: "/openservice/schedule/sourcemeta",
          routeCondition: "/openservice/**",
          activeCondition: "/openservice/schedule/sourcemeta",
          permission: OpenService.Permissions.Access.global,
        });
    } catch (error) {
      console.error("Error fetching source meta:", error);
    }

    factory.registerStaticNavigationItem({
      id: "openservice/machinelog",
      group: "statistics",
      place: "sidebar",
      order: 10,
      label: "maintenance:menu.statistics.machinelog_label",
      icon: "fa:book",
      link: "/openservice/machinelog",
      routeCondition: "/openservice/**",
      activeCondition: "/openservice/machinelog",
      permission: OpenService.Permissions.Access.global,
    });

    // add when ready
    // if ($parse.server.config.FEATURE_MONITORING) {
    //   factory.registerStaticNavigationItem({
    //     id: "openservice/monitoring",
    //     group: "statistics",
    //     place: "sidebar",
    //     order: 20,
    //     label: "maintenance:menu.statistics.monitoring_label",
    //     icon: "fa:chart-line",
    //     link: "/openservice/monitoring",
    //     routeCondition: "/openservice/**",
    //     activeCondition: "/openservice/monitoring",
    //     permission: [
    //       OpenService.Permissions.Access.global,
    //       OpenService.Permissions.Access.monitoring,
    //     ],
    //   });
    // }

    const [itemError, item] = await catchError(new Parse.Query(Item).first());
    if (item)
      factory.registerStaticNavigationItem({
        id: "openservice/inventory/items",
        group: "inventory",
        place: "sidebar",
        order: 10,
        label: "maintenance:menu.inventory.items_label",
        icon: "fa:bars",
        link: "/openservice/inventory/items",
        routeCondition: "/openservice/**",
        activeCondition: "/openservice/inventory/items",
        permission: OpenService.Permissions.Access.global,
      });

    const [fileError, manual] = await catchError(
      new Parse.Query(Source_File).first()
    );
    if (manual)
      factory.registerStaticNavigationItem({
        id: "openservice/knowledge/manuals",
        group: "inventory",
        place: "sidebar",
        order: 11,
        label: "maintenance:menu.inventory.manual_label",
        icon: "fa:bars",
        link: "/openservice/knowledge/manuals",
        routeCondition: "/openservice/**",
        activeCondition: "/openservice/knowledge/manuals",
        permission: OpenService.Permissions.Access.global,
      });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/general",
      group: "openservice_admin",
      place: "sidebar",
      order: 1,
      label: "maintenance:menu.general.title",
      icon: "fa:cog",
      link: "/admin/openservice/general?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/openservice/general",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administation/ticketform",
      group: "openservice_admin",
      place: "sidebar",
      order: 2,
      label: "maintenance:menu.admin.ticket_creation",
      icon: "fa:newspaper",
      link: "/admin/parse/OD3_Maintenance_Ticket_FormConfig?hideSourcePicker=true",
      activeCondition: "/admin/parse/OD3_Maintenance_Ticket_FormConfig",
      routeCondition: "/admin/**",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administation/qrcode",
      group: "openservice_admin",
      place: "sidebar",
      order: 10,
      label: "maintenance:menu.admin.qrcode_label",
      icon: "fa:qrcode",
      link: "/admin/parse/OD3_Maintenance_Ticket_QR_Code?hideSourcePicker=true",
      activeCondition: "/admin/parse/OD3_Maintenance_Ticket_QR_Code",
      routeCondition: "/admin/**",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/schedule/templates",
      group: "openservice_admin",
      place: "sidebar",
      order: 3,
      label: "maintenance:menu.administration.schedules.label",
      icon: "fa:file-spreadsheet",
      link: "/admin/parse/OD3_Maintenance_Schedule_Template?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Maintenance_Schedule_Template",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/schedule/steps",
      group: "openservice_admin",
      place: "sidebar",
      order: 4,
      label: "maintenance:menu.administration.schedule.step_label",
      icon: "fa:file-spreadsheet",
      link: "/admin/parse/OD3_Maintenance_Schedule_Step?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Maintenance_Schedule_Step",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/items",
      group: "openservice_admin",
      place: "sidebar",
      order: 5,
      label: "maintenance:menu.administration.items_label",
      icon: "fa:bars",
      link: "/admin/parse/OD3_Maintenance_Item?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Maintenance_Item",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/parse/projects",
      group: "openservice_admin",
      place: "sidebar",
      order: 2,
      label: "maintenance:menu.administration.projects",
      icon: "fa:file-chart-line",
      link: "/admin/parse/OD3_Maintenance_Project?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Maintenance_Project",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/parse/contacts",
      group: "openservice_admin",
      place: "sidebar",
      order: 6,
      label: "maintenance:menu.administration.contacts.label",
      icon: "fa:address-book",
      link: "/admin/parse/OD3_Contact?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Contact",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/parse/company",
      group: "openservice_admin",
      place: "sidebar",
      order: 7,
      label: "maintenance:parse.company",
      icon: "fa:home",
      link: "/admin/parse/OD3_Company?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Company",
      permission: OpenService.Permissions.Access.global,
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/categories",
      group: "openservice_admin",
      place: "sidebar",
      order: 8,
      label: "maintenance:menu.administration.issuecategory_label",
      icon: "fa:sitemap",
      link: "/admin/parse/OD3_Maintenance_Issuecategory?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/parse/OD3_Maintenance_Issuecategory",
      permission: [OpenService.Permissions.Access.global],
    });

    factory.registerStaticNavigationItem({
      id: "openservice/administration/monitor",
      group: "openservice_admin",
      place: "sidebar",
      order: 9,
      label: "maintenance:menu.administration.monitorstate_label",
      icon: "fa:map-pin",
      link: "/admin/openservice/monitor?hideSourcePicker=true",
      routeCondition: "/admin/**",
      activeCondition: "/admin/openservice/monitor",
      permission: [
        OpenService.Permissions.Access.global,
        OpenService.Permissions.Access.monitor,
      ],
    });

    const count = await new Parse.Query(Project).count();
    if (count > 0) {
      factory.registerStaticNavigationItem({
        id: "openservice/projects",
        group: "tickets",
        place: "sidebar",
        order: 10,
        label: "maintenance:menu.tickets.projects_label",
        icon: "fa:file-chart-line",
        link: "/openservice/projects",
        routeCondition: "/openservice/**",
        activeCondition: "/openservice/projects",
        permission: OpenService.Permissions.Access.global,
      });
    }
  }
}
